import type { PlayerSessionSummary } from "@utopia-well-done-ltd/contracts";
import { useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { asMoment } from "../format.js";

/** Live, revoked, or simply expired — three different facts an investigation cares about. */
function state(session: PlayerSessionSummary): { label: string; tone: string } {
  if (session.revokedAt) return { label: "revoked", tone: "banned" };
  if (new Date(session.expiresAt) < new Date()) return { label: "expired", tone: "suspended" };
  return { label: "live", tone: "active" };
}

/**
 * Every sign-in the account has made, revoked ones included.
 *
 * The revoked rows are the useful ones: "someone else was in my account" is answered by what was
 * there before the password changed, not by what is there now. This is also the most sensitive
 * screen the panel shows without money — a list of the addresses a person signed in from — which is
 * why the API records opening it as its own audited read.
 */
export function SessionList({ playerId }: { playerId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<PlayerSessionSummary[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // Cleared before the fetch starts, not just on error: without this, switching players shows
    // the previous player's sessions — their IPs and devices — mislabeled as the new player's,
    // for as long as the new fetch takes.
    setSessions(null);
    setError(null);

    api
      .playerSessions(playerId, controller.signal)
      .then(({ sessions: found }) => setSessions(found))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught.message : "Could not load sessions.");
      });

    return () => controller.abort();
  }, [playerId]);

  if (error)
    return (
      <p className="banner" role="alert">
        {error}
      </p>
    );
  if (!sessions) return <p className="empty">Loading…</p>;
  if (sessions.length === 0) return <p className="empty">This account has never signed in.</p>;

  return (
    <table className="mobile-cards">
      <thead>
        <tr>
          <th scope="col" style={{ width: "16%" }}>
            State
          </th>
          <th scope="col" style={{ width: "16%" }}>
            Address
          </th>
          <th scope="col">Device</th>
          <th scope="col" style={{ width: "20%" }}>
            Started
          </th>
          <th scope="col" style={{ width: "20%" }}>
            Last seen
          </th>
        </tr>
      </thead>
      <tbody>
        {sessions.map((session) => {
          const { label, tone } = state(session);
          return (
            <tr key={session.id}>
              <td data-label="State">
                <span className={`badge badge-${tone}`}>
                  <span className="dot" />
                  {label}
                </span>
              </td>
              <td className="mono-12" data-label="Address">
                {session.ip ?? "—"}
              </td>
              <td
                className="label-13 dim truncate"
                data-label="Device"
                title={session.userAgent ?? undefined}
              >
                {session.userAgent ?? "—"}
              </td>
              <td className="label-13 dim" data-label="Started">
                {asMoment(session.createdAt)}
              </td>
              <td className="label-13 dim" data-label="Last seen">
                {asMoment(session.lastSeenAt)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
